import React, { ReactNode } from 'react';

import { useQuery } from '@apollo/client/react';
import { c } from 'ttag';

import { Button, LoaderPage, useAuthentication } from '@freelancelabs/components';
import { LogoFreelance } from '@freelancelabs/icons';
import { marquerSortie } from '@freelancelabs/shared';

import { RoleScope } from '../graphql/__generated__/schema';
import { ViewerDocument } from '../graphql/__generated__/types';

interface Props {
    children: ReactNode;
}

/**
 * Admission to the back-office, checked once at the root rather than route by route.
 * The discriminator is `role.scope` from our own database, not an inside-api `appScope`
 * (those are indexed by Cognito app client, and the three front apps share one, so it
 * separates nothing). This guard decides what is displayed, never what is readable: the
 * boundary is the API plus the access restriction on this host.
 */
const BackofficeGuard = ({ children }: Props) => {
    const { logout } = useAuthentication();
    const { data, loading, error } = useQuery(ViewerDocument);

    const memberships = data?.viewer?.memberships ?? [];
    const isStaff = memberships.some((membership) => membership?.role?.scope === RoleScope.Backoffice);

    // Only the confirmed-refusal branch logs out; `error` never does (we do not yet
    // know, and evicting on an unanswered query wrongly ejects a valid admin when the
    // API is down).
    const refuse = !loading && !error && !isStaff;
    // If logout fails, fall back to the panel rather than a spinner forever. Do not
    // redirect anyway: the session is still open, so `account` would send the person
    // back here.
    const [sortieEnEchec, setSortieEnEchec] = React.useState(false);
    React.useEffect(() => {
        if (!refuse || sortieEnEchec) {
            return;
        }
        // Mark then log out; do not redirect from here, `logout()` clears the session so
        // `Setup` leaves for `account` on its own.
        marquerSortie('APP_UNAUTHORIZED');
        void logout().catch(() => setSortieEnEchec(true));
    }, [refuse, sortieEnEchec, logout]);

    if (loading || (refuse && !sortieEnEchec)) {
        return <LoaderPage />;
    }

    // Not knowing is not refusing, and this panel says both without confusing them: a
    // query failure must eject nobody, so its message speaks of the failed read, not of
    // the person's memberships.
    if (error || refuse) {
        return (
            <main className="flex flex-1 h-full items-center justify-center flex-col gap-6 p-6">
                <LogoFreelance size={96} />
                <h1 className="text-xl font-semibold text-text text-center">
                    {error
                        ? c('Back-office').t`Your permissions could not be checked.`
                        : c('Back-office').t`This area is reserved for Freelance.com staff.`}
                </h1>
                <p className="text-text-600 text-center max-w-md">
                    {error
                        ? c('Back-office')
                              .t`This says nothing about your account: the query that reads your memberships did not answer. Reload the page, and tell your administrator if it persists.`
                        : c('Back-office')
                              .t`Your account does not carry an internal membership. If you think this is a mistake, contact your administrator.`}
                </p>
                {/* The server response, verbatim: for whoever will fix it, not the user. */}
                {error && <p className="text-xs text-text-hint text-center max-w-md break-words">{error.message}</p>}
                <div className="flex flex-wrap items-center justify-center gap-3">
                    {error && <Button onClick={() => window.location.reload()}>{c('Action').t`Reload`}</Button>}
                    <Button onClick={logout} category="weak">
                        {c('Action').t`Log out`}
                    </Button>
                </div>
            </main>
        );
    }

    return <>{children}</>;
};

export default BackofficeGuard;
